import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import api from "../../utils/api";

const FileEditModal = ({ file, onClose, onSuccess }) => {
  const [fileName, setFileName] = useState(file.file_name);
  const [fileCategory, setFileCategory] = useState(file.file_category);
  const [phase, setPhase] = useState(file.phase);
  const [roleSpecific, setRoleSpecific] = useState(file.role_specific || '');
  const [employeeSpecificIds, setEmployeeSpecificIds] = useState(file.employee_specific_ids || []);
  const [reviewByDate, setReviewByDate] = useState(file.review_by_date ? file.review_by_date.split('T')[0] : '');
  const [requiresAcknowledgment, setRequiresAcknowledgment] = useState(file.requires_acknowledgment || false);
  const [updating, setUpdating] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [roles, setRoles] = useState([]);

  useEffect(() => {
    if (fileCategory === 'employee-specific') {
      fetchEmployees();
    }
    if (fileCategory === 'role-specific') {
      fetchRoles();
    }
  }, [fileCategory]);

  const fetchEmployees = async () => {
    try {
      const response = await api.get(`/employees`);
      if (response.data.employees) {
        setEmployees(response.data.employees);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await api.get(`/employees/roles`);
      if (response.data.roles) {
        setRoles(response.data.roles);
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  const handleUpdate = async () => {
    if (!fileName.trim()) {
      toast.error('File name is required');
      return;
    }

    if (fileCategory === 'role-specific' && !roleSpecific) {
      toast.error('Please select a role');
      return;
    }

    if (fileCategory === 'employee-specific' && employeeSpecificIds.length === 0) {
      toast.error('Please select at least one employee');
      return;
    }

    try {
      setUpdating(true);

      const updateData = {
        file_name: fileName,
        file_category: fileCategory,
        phase,
        role_specific: fileCategory === 'role-specific' ? roleSpecific : null,
        employee_specific_ids: fileCategory === 'employee-specific' ? employeeSpecificIds : null,
        review_by_date: reviewByDate || null,
        requires_acknowledgment: requiresAcknowledgment,
      };

      const response = await api.put(
        `/files/company/${file.id}`,
        updateData
      );

      onSuccess();
    } catch (error) {
      console.error('Update error:', error);
      toast.error(error.response?.data?.message || 'Failed to update file');
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Edit File Metadata</h2>
            <p className="text-sm text-gray-600 mt-1">Update file information and settings</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl">
            ×
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* File Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              File Name *
            </label>
            <input
              type="text"
              value={fileName}
              onChange={(e) => setFileName(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="Enter file name"
            />
          </div>

          {/* Phase / Lifecycle Stage */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Lifecycle Stage *</label>
            <div className="grid grid-cols-3 gap-3">
              {[
                { value: 'preboarding', label: 'Preboarding', color: 'amber' },
                { value: 'onboarding', label: 'Onboarding', color: 'blue' },
                { value: 'both', label: 'Both', color: 'purple' },
              ].map(({ value, label, color }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setPhase(value)}
                  className={`p-3 border-2 rounded-xl text-center transition-all ${
                    phase === value
                      ? 'border-current'
                      : 'border-gray-200 hover:border-gray-300 text-gray-600'
                  }`}
                  style={phase === value ? {
                    borderColor: color === 'amber' ? '#f59e0b' : color === 'blue' ? '#3b82f6' : '#8b5cf6',
                    backgroundColor: color === 'amber' ? '#fffbeb' : color === 'blue' ? '#eff6ff' : '#f5f3ff',
                    color: color === 'amber' ? '#b45309' : color === 'blue' ? '#1d4ed8' : '#6d28d9'
                  } : {}}
                >
                  <span className="font-medium text-sm">{label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Category Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              File Category *
            </label>
            <select
              value={fileCategory}
              onChange={(e) => {
                setFileCategory(e.target.value);
                // Reset related fields when category changes
                if (e.target.value !== 'role-specific') setRoleSpecific('');
                if (e.target.value !== 'employee-specific') setEmployeeSpecificIds([]);
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="company-wide">Company-Wide Files</option>
              <option value="role-specific">Role-Specific Files</option>
              <option value="employee-specific">Employee-Specific Files</option>
              <option value="private">Private Files (Admin Only)</option>
            </select>
          </div>

          {/* Role Selection (if role-specific) */}
          {fileCategory === 'role-specific' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Role *
              </label>
              <select
                value={roleSpecific}
                onChange={(e) => setRoleSpecific(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="">Select a role...</option>
                {roles.map((role) => (
                  <option key={role.id} value={role.role_name}>
                    {role.role_name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Employee Selection (if employee-specific) */}
          {fileCategory === 'employee-specific' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Employees *
              </label>
              <div className="border border-gray-300 rounded-lg p-4 max-h-48 overflow-y-auto">
                {employees.length === 0 ? (
                  <p className="text-sm text-gray-500">Loading employees...</p>
                ) : (
                  employees.map((employee) => (
                    <label
                      key={employee.id}
                      className="flex items-center gap-2 py-2 hover:bg-gray-50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={employeeSpecificIds.includes(employee.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setEmployeeSpecificIds([...employeeSpecificIds, employee.id]);
                          } else {
                            setEmployeeSpecificIds(
                              employeeSpecificIds.filter((id) => id !== employee.id)
                            );
                          }
                        }}
                        className="w-4 h-4 text-primary rounded focus:ring-2 focus:ring-primary"
                      />
                      <span className="text-sm text-gray-700">
                        {employee.first_name} {employee.last_name} ({employee.email})
                      </span>
                    </label>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Review By Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Review By Date
            </label>
            <input
              type="date"
              value={reviewByDate}
              onChange={(e) => setReviewByDate(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">
              Set a date when this document should be reviewed for updates
            </p>
          </div>

          {/* Compliance Options */}
          <div className="p-4 rounded-xl bg-gray-50 border border-gray-100">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={requiresAcknowledgment}
                onChange={(e) => setRequiresAcknowledgment(e.target.checked)}
                className="w-5 h-5 text-primary rounded focus:ring-primary border-gray-300"
              />
              <div>
                <span className="block text-sm font-medium text-gray-900">Require Employee Acknowledgment</span>
                <span className="block text-xs text-gray-500">Employees must mark this file as "Reviewed"</span>
              </div>
            </label>
          </div>

          {/* File Info (Read-only) */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
            <h3 className="text-sm font-medium text-gray-700 mb-2">File Information</h3>
            <div className="text-sm text-gray-600">
              <span className="font-medium">Type:</span> {file.file_type}
            </div>
            <div className="text-sm text-gray-600">
              <span className="font-medium">Size:</span>{' '}
              {file.file_size
                ? `${(file.file_size / 1024 / 1024).toFixed(2)} MB`
                : 'Unknown'}
            </div>
            <div className="text-sm text-gray-600">
              <span className="font-medium">Uploaded:</span>{' '}
              {new Date(file.created_at).toLocaleString()}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 px-6 py-4 flex items-center justify-end gap-3 border-t border-gray-200">
          <button
            onClick={onClose}
            disabled={updating}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleUpdate}
            disabled={updating}
            className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {updating ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Updating...
              </>
            ) : (
              'Update File'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FileEditModal;

